/**
 * Shared AddressFinder lookup — POW-190.
 *
 * WHY THIS CALLS ADDRESSFINDER FROM THE BROWSER
 * ---------------------------------------------
 * Our AddressFinder plan permits **widget/browser** calls but rejects
 * server-to-server ones:
 *
 *     "Direct API calls are not permitted on your current plan"
 *
 * POW-187 routed the System Detail lookup through the backend proxy
 * (/api/address/*), which calls AddressFinder server-side. That is exactly the
 * mode the plan refuses, so in production every search returned nothing and
 * users saved free text with no coordinates. Onboarding never had this problem
 * because it has always called the API directly from the browser.
 *
 * The key is a build-time constant, not a runtime secret: craco.config.js
 * injects `process.env.ADDRESSFINDER_KEY` through CRA's DefinePlugin, and
 * frontend/Dockerfile passes it as a build ARG. It is public by design — an
 * AddressFinder widget key is domain-restricted at their end.
 *
 * Extracted from Onboard.react.js so both pages share one implementation.
 *
 * NOTE ON RAW fetch(): the repo rule is to use `@/libs/api` so requests carry
 * auth and a 401 redirects to login. That rule is for OUR backend. These two
 * calls go to api.addressfinder.io, a third party — sending our JWT there would
 * leak it, and their 401 has nothing to do with our session. Onboarding has
 * always used raw fetch here for the same reason.
 */

import { useCallback, useRef, useState } from "react";
import debounce from "lodash-es/debounce";

const AF_KEY = process.env.ADDRESSFINDER_KEY || "";
const AF_BASE = "https://api.addressfinder.io/api/au/address";

/** AddressFinder returns abbreviations; the DB stores full state names. */
export const STATE_ABBREV_TO_FULL = {
  NSW: "New South Wales",
  VIC: "Victoria",
  QLD: "Queensland",
  SA: "South Australia",
  WA: "Western Australia",
  TAS: "Tasmania",
  NT: "Northern Territory",
  ACT: "Australian Capital Territory",
};

/**
 * @returns {{
 *   options: Array<{value: string, label: string, data: {id: string}}>,
 *   searching: boolean,
 *   search: (text: string) => void,
 *   resolve: (addressId: string) => Promise<object|null>,
 *   clear: () => void,
 *   configured: boolean,
 * }}
 *
 * `resolve` returns the structured address INCLUDING latitude/longitude, or
 * null when the lookup fails. Callers must treat null as "no coordinates" —
 * see the free-text rule in SiteAddressCard.
 */
export default function useAddressFinder() {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  // debounce() must not be recreated per render or it never debounces.
  const searchRef = useRef(null);
  if (!searchRef.current) {
    searchRef.current = debounce(async (text, setOpts, setBusy) => {
      if (!text || text.length < 3) {
        setOpts([]);
        return;
      }
      setBusy(true);
      try {
        const params = new URLSearchParams({
          key: AF_KEY,
          q: text,
          format: "json",
          max: "5",
        });
        const resp = await fetch(`${AF_BASE}/autocomplete?${params}`);
        const data = await resp.json();
        setOpts(
          (data.completions || []).map((item) => ({
            value: item.full_address,
            label: item.full_address,
            data: { id: item.id, full_address: item.full_address },
          }))
        );
      } catch (err) {
        console.error("AddressFinder search failed:", err);
        setOpts([]);
      } finally {
        setBusy(false);
      }
    }, 300);
  }

  const search = useCallback((text) => {
    searchRef.current(text, setOptions, setSearching);
  }, []);

  const resolve = useCallback(async (addressId) => {
    if (!addressId) return null;
    try {
      const params = new URLSearchParams({ key: AF_KEY, id: addressId, format: "json" });
      const resp = await fetch(`${AF_BASE}/info?${params}`);
      const d = await resp.json();
      if (!d || !d.full_address) return null;
      return {
        full_address: d.full_address,
        address_line_1: d.address_line_1 || d.address_line_combined || "",
        locality_name: d.locality_name || "",
        state_territory: d.state_territory || "",
        postcode: d.postcode || "",
        latitude: d.latitude ?? null,
        longitude: d.longitude ?? null,
      };
    } catch (err) {
      console.error("AddressFinder lookup failed:", err);
      return null;
    }
  }, []);

  const clear = useCallback(() => setOptions([]), []);

  return { options, searching, search, resolve, clear, configured: Boolean(AF_KEY) };
}
